import { Button, CheckIcon, Dialog, DialogButtons, Select, Spinner } from '@st/theme'
import { formatMoney } from '@st/util/format'
import { ReactNode, useState } from 'react'
import { CURRENT_PROMOTION, NUMBER_OF_CREDITS_OPTIONS, Promotion } from './config'
import { useOrganizationBilling } from './hooks'
import { routeModule } from '~/features/route-module'
import { useSelect } from '~/store'

type Props = {
  continueUrl?: string
  onClose: () => void
}
export function PurchaseCreditsDialog({ continueUrl, onClose }: Props) {
  const [state, send] = useOrganizationBilling()
  const location = useSelect('route', (s) => s.location)
  const defaultContinueUrl = `${location.origin}${location.pathname}`

  if (state.status != 'loaded') {
    return null
  }

  return (
    <PurchaseCreditsDialogView
      onClose={onClose}
      creditsUnitPrice={state.billingState.creditsUnitPrice}
      creditOptions={NUMBER_OF_CREDITS_OPTIONS}
      promotion={CURRENT_PROMOTION}
      isGeneratingCheckoutLink={state.checkout.status == 'generating'}
      checkoutFailed={state.checkout.status == 'failed'}
      onContinue={(quantity) => {
        send({
          type: 'startPurchaseCreditsCheckoutFlow',
          quantity,
          continueUrl: continueUrl ?? defaultContinueUrl
        })
      }}
    />
  )
}

type PurchaseCreditsDialogProps = {
  creditsUnitPrice: number
  creditOptions: number[]
  promotion: Promotion | undefined
  onContinue: (quantity: number) => void
  onClose: () => void
  isGeneratingCheckoutLink: boolean
  checkoutFailed: boolean
}

function PurchaseCreditsDialogView({
  creditsUnitPrice,
  creditOptions,
  promotion,
  onContinue,
  onClose,
  isGeneratingCheckoutLink,
  checkoutFailed
}: PurchaseCreditsDialogProps) {
  const [selectedCreditQuantity, setSelectedCreditQuantity] = useState<number | undefined>()

  return (
    <Dialog
      title="Purchase credits"
      subtitle={
        <span>
          {`Please select the number of credits you'd like to purchase. Each return costs one credit. Unused credits will
          roll over to the following year.`}
        </span>
      }
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={isGeneratingCheckoutLink ? <Spinner className="size-4" /> : undefined}
            disabled={selectedCreditQuantity == undefined || isGeneratingCheckoutLink}
            onClick={() => {
              if (selectedCreditQuantity != undefined) {
                onContinue(selectedCreditQuantity)
              }
            }}
          >
            Continue
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col items-start p-2">
        <Select
          placeholder="Select number of credits"
          options={creditOptions}
          value={selectedCreditQuantity}
          onChange={(quantity) => setSelectedCreditQuantity(quantity as number)}
          buildValue={(quantity: number) => quantity}
          buildLabel={(quantity: number) => `${quantity} credits`}
        />
        {checkoutFailed && (
          <div className="mt-4 text-sm font-medium text-red-700" role="alert">
            We couldn't open checkout. Please try again.
          </div>
        )}
        {selectedCreditQuantity !== undefined && (
          <ul className="mt-4 space-y-1 text-sm/6 text-gray-600">
            {promotion ? (
              <PurchaseSummaryItem>
                <span className="line-through">
                  {formatMoney(selectedCreditQuantity * creditsUnitPrice)}
                </span>{' '}
                {formatMoney(selectedCreditQuantity * creditsUnitPrice * (1 - promotion.discount))}{' '}
                before {promotion.before} (code: {promotion.coupon})
              </PurchaseSummaryItem>
            ) : (
              <PurchaseSummaryItem>
                Total price: {formatMoney(selectedCreditQuantity * creditsUnitPrice)}
              </PurchaseSummaryItem>
            )}
            <PurchaseSummaryItem>No implementation fees</PurchaseSummaryItem>
            <PurchaseSummaryItem>
              Unused credits roll over to the following year
            </PurchaseSummaryItem>
          </ul>
        )}
      </div>
    </Dialog>
  )
}

function PurchaseSummaryItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-x-3 text-base">
      <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
      {children}
    </li>
  )
}
